import { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Form,
  Row,
} from 'react-bootstrap';
import { useCourseEmailTemplates } from '@hooks';
import {
  COURSE_EMAIL_SAMPLE_VARIABLES,
  COURSE_EMAIL_TEMPLATE_DEFINITIONS,
  COURSE_EMAIL_TEMPLATE_TYPES,
  COURSE_EMAIL_VARIABLE_DEFINITIONS,
  extractTemplateVariables,
  renderEmailTemplate,
} from '@config/emailTemplates';

const createInitialEditorState = () => ({
  subject: '',
  body: '',
  isEnabled: true,
});

const formatDateTime = (value) => {
  if (!value) return 'Default template';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Unknown';

  return date.toLocaleString();
};

const buildPreviewVariables = (courseTitle) => ({
  ...COURSE_EMAIL_SAMPLE_VARIABLES,
  courseName: courseTitle || COURSE_EMAIL_SAMPLE_VARIABLES.courseName,
});

export function CourseEmailTemplateManager({ courseId, courseTitle }) {
  const {
    loading,
    saving,
    sendingReminders,
    error,
    supportedVariables,
    templateTypes,
    upsertTemplate,
    resetTemplate,
    sendStudyReminders,
    getTemplateByType,
    clearError,
  } = useCourseEmailTemplates(courseId);

  const [selectedTemplateType, setSelectedTemplateType] = useState(COURSE_EMAIL_TEMPLATE_TYPES[0]);
  const [editorState, setEditorState] = useState(createInitialEditorState);
  const [previewVariables, setPreviewVariables] = useState(buildPreviewVariables(courseTitle));
  const [focusedField, setFocusedField] = useState('body');
  const [successMessage, setSuccessMessage] = useState(null);
  const [reminderDays, setReminderDays] = useState(3);
  const [maxReminderRecipients, setMaxReminderRecipients] = useState(100);
  const [reminderResult, setReminderResult] = useState(null);

  const subjectRef = useRef(null);
  const bodyRef = useRef(null);

  const effectiveTemplateTypes = useMemo(() => {
    if (Array.isArray(templateTypes) && templateTypes.length > 0) {
      return templateTypes;
    }
    return COURSE_EMAIL_TEMPLATE_TYPES;
  }, [templateTypes]);

  useEffect(() => {
    if (!effectiveTemplateTypes.includes(selectedTemplateType)) {
      setSelectedTemplateType(effectiveTemplateTypes[0]);
    }
  }, [effectiveTemplateTypes, selectedTemplateType]);

  useEffect(() => {
    setPreviewVariables(buildPreviewVariables(courseTitle));
  }, [courseId, courseTitle]);

  const selectedTemplate = useMemo(
    () => getTemplateByType(selectedTemplateType),
    [getTemplateByType, selectedTemplateType]
  );

  useEffect(() => {
    if (!selectedTemplate) {
      setEditorState(createInitialEditorState());
      return;
    }

    setEditorState({
      subject: selectedTemplate.subject || '',
      body: selectedTemplate.body || '',
      isEnabled: selectedTemplate.isEnabled !== false,
    });
  }, [selectedTemplate]);

  const knownVariables = useMemo(
    () => ({
      ...COURSE_EMAIL_VARIABLE_DEFINITIONS,
      ...(supportedVariables || {}),
    }),
    [supportedVariables]
  );

  const availableVariableNames = useMemo(() => {
    const templateVariables = Array.isArray(selectedTemplate?.availableVariables)
      ? selectedTemplate.availableVariables
      : [];

    const globalVariables = Object.keys(knownVariables);
    return Array.from(new Set([...templateVariables, ...globalVariables]));
  }, [knownVariables, selectedTemplate?.availableVariables]);

  const discoveredVariables = useMemo(() => {
    const found = new Set([
      ...extractTemplateVariables(editorState.subject),
      ...extractTemplateVariables(editorState.body),
    ]);
    return Array.from(found);
  }, [editorState.body, editorState.subject]);

  const unknownVariables = useMemo(
    () => discoveredVariables.filter((variable) => !Object.prototype.hasOwnProperty.call(knownVariables, variable)),
    [discoveredVariables, knownVariables]
  );

  const isDirty = useMemo(() => {
    if (!selectedTemplate) return false;

    return (
      editorState.subject !== (selectedTemplate.subject || '')
      || editorState.body !== (selectedTemplate.body || '')
      || editorState.isEnabled !== (selectedTemplate.isEnabled !== false)
    );
  }, [editorState, selectedTemplate]);

  const renderedSubject = useMemo(
    () => renderEmailTemplate(editorState.subject, previewVariables),
    [editorState.subject, previewVariables]
  );

  const renderedBody = useMemo(
    () => renderEmailTemplate(editorState.body, previewVariables),
    [editorState.body, previewVariables]
  );

  const insertVariableToken = (variableName) => {
    const token = `{{${variableName}}}`;
    const targetField = focusedField === 'subject' ? 'subject' : 'body';
    const targetRef = targetField === 'subject' ? subjectRef.current : bodyRef.current;

    if (!targetRef || typeof targetRef.selectionStart !== 'number') {
      setEditorState((prev) => ({
        ...prev,
        [targetField]: `${prev[targetField] || ''}${token}`,
      }));
      return;
    }

    const start = targetRef.selectionStart;
    const end = targetRef.selectionEnd;

    setEditorState((prev) => {
      const value = prev[targetField] || '';
      return {
        ...prev,
        [targetField]: `${value.slice(0, start)}${token}${value.slice(end)}`,
      };
    });

    const nextCaretPosition = start + token.length;
    setTimeout(() => {
      targetRef.focus();
      targetRef.setSelectionRange(nextCaretPosition, nextCaretPosition);
    }, 0);
  };

  const handleSave = async () => {
    if (!selectedTemplateType) return;

    try {
      await upsertTemplate(selectedTemplateType, {
        subject: editorState.subject,
        body: editorState.body,
        isEnabled: editorState.isEnabled,
      });

      setSuccessMessage('Email template saved successfully.');
      setTimeout(() => setSuccessMessage(null), 2500);
    } catch {
      // Error state is managed by the hook.
    }
  };

  const handleResetToDefault = async () => {
    if (!selectedTemplateType) return;

    const confirmed = window.confirm(
      'Reset this template to the default version? This removes your custom subject and body for this course.'
    );

    if (!confirmed) return;

    try {
      await resetTemplate(selectedTemplateType);
      setSuccessMessage('Template reset to default.');
      setTimeout(() => setSuccessMessage(null), 2500);
    } catch {
      // Error state is managed by the hook.
    }
  };

  const handleRevertChanges = () => {
    if (!selectedTemplate) return;

    setEditorState({
      subject: selectedTemplate.subject || '',
      body: selectedTemplate.body || '',
      isEnabled: selectedTemplate.isEnabled !== false,
    });
  };

  const handleSendStudyReminders = async () => {
    if (!courseId) return;

    try {
      const result = await sendStudyReminders({
        idleDays: Number(reminderDays),
        maxRecipients: Number(maxReminderRecipients),
      });

      setReminderResult(result || null);
      setSuccessMessage('Study reminders processed successfully.');
      setTimeout(() => setSuccessMessage(null), 2500);
    } catch {
      // Error state is managed by the hook.
    }
  };

  const disabledActions = saving || loading || !selectedTemplate;
  const missingContent = !editorState.subject.trim() || !editorState.body.trim();

  return (
    <div>
      {successMessage && (
        <Alert variant="success" dismissible onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {error && (
        <Alert variant="danger" dismissible onClose={clearError}>
          {error}
        </Alert>
      )}

      <Row className="g-3">
        <Col lg={7}>
          <Card className="h-100">
            <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
              <div>
                <strong>Email Template Editor</strong>
                <div className="text-muted small">Course: {courseTitle || courseId}</div>
              </div>
              {selectedTemplate && (
                <div className="d-flex align-items-center gap-2">
                  <Badge bg={selectedTemplate.isDefault ? 'secondary' : 'primary'}>
                    {selectedTemplate.isDefault ? 'Default' : 'Custom'}
                  </Badge>
                  <Badge bg={selectedTemplate.isEnabled ? 'success' : 'warning'} text={selectedTemplate.isEnabled ? undefined : 'dark'}>
                    {selectedTemplate.isEnabled ? 'Enabled' : 'Disabled'}
                  </Badge>
                </div>
              )}
            </Card.Header>

            <Card.Body>
              <Form.Group className="mb-3">
                <Form.Label>Template Type</Form.Label>
                <Form.Select
                  value={selectedTemplateType}
                  onChange={(event) => setSelectedTemplateType(event.target.value)}
                  disabled={loading || saving}
                >
                  {effectiveTemplateTypes.map((templateType) => {
                    const definition = COURSE_EMAIL_TEMPLATE_DEFINITIONS[templateType] || {
                      label: templateType,
                      description: '',
                    };

                    return (
                      <option key={templateType} value={templateType}>
                        {definition.label}
                      </option>
                    );
                  })}
                </Form.Select>
                <Form.Text className="text-muted">
                  {selectedTemplate?.description || COURSE_EMAIL_TEMPLATE_DEFINITIONS[selectedTemplateType]?.description}
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Check
                  type="switch"
                  id="email-template-enabled"
                  label="Template enabled"
                  checked={editorState.isEnabled}
                  onChange={(event) => setEditorState((prev) => ({ ...prev, isEnabled: event.target.checked }))}
                  disabled={loading || saving}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Subject</Form.Label>
                <Form.Control
                  ref={subjectRef}
                  type="text"
                  value={editorState.subject}
                  onFocus={() => setFocusedField('subject')}
                  onChange={(event) => setEditorState((prev) => ({ ...prev, subject: event.target.value }))}
                  disabled={loading || saving}
                  placeholder="Email subject"
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Body</Form.Label>
                <Form.Control
                  ref={bodyRef}
                  as="textarea"
                  rows={14}
                  value={editorState.body}
                  onFocus={() => setFocusedField('body')}
                  onChange={(event) => setEditorState((prev) => ({ ...prev, body: event.target.value }))}
                  disabled={loading || saving}
                  placeholder="Email body"
                />
                <Form.Text className="text-muted">
                  You can use plain text or simple HTML. Keep content concise and include a clear next action.
                </Form.Text>
              </Form.Group>

              <div className="mb-3">
                <div className="fw-semibold mb-2">Insert Variables</div>
                <div className="d-flex flex-wrap gap-2">
                  {availableVariableNames.map((variableName) => (
                    <Button
                      key={variableName}
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => insertVariableToken(variableName)}
                      disabled={loading || saving}
                      title={knownVariables[variableName] || variableName}
                    >
                      {`{{${variableName}}}`}
                    </Button>
                  ))}
                </div>
                <div className="text-muted small mt-2">
                  Variables are inserted at the cursor in the {focusedField} field.
                </div>
              </div>

              {unknownVariables.length > 0 && (
                <Alert variant="warning" className="mb-3">
                  Unknown variable(s): {unknownVariables.join(', ')}
                </Alert>
              )}

              <div className="d-flex flex-wrap gap-2 align-items-center">
                <Button
                  variant="primary"
                  onClick={handleSave}
                  disabled={disabledActions || missingContent || !isDirty}
                >
                  Save Template
                </Button>
                <Button
                  variant="outline-secondary"
                  onClick={handleRevertChanges}
                  disabled={disabledActions || !isDirty}
                >
                  Revert Changes
                </Button>
                <Button
                  variant="outline-danger"
                  onClick={handleResetToDefault}
                  disabled={disabledActions}
                >
                  Reset to Default
                </Button>
              </div>

              <div className="text-muted small mt-3">
                Last update: {formatDateTime(selectedTemplate?.updatedAt)}
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={5}>
          <Card className="mb-3">
            <Card.Header>
              <strong>Preview Variables</strong>
            </Card.Header>
            <Card.Body>
              <Row className="g-2">
                {availableVariableNames.map((variableName) => (
                  <Col key={variableName} md={6}>
                    <Form.Group>
                      <Form.Label className="small mb-1">{variableName}</Form.Label>
                      <Form.Control
                        size="sm"
                        value={previewVariables[variableName] || ''}
                        onChange={(event) => {
                          const value = event.target.value;
                          setPreviewVariables((prev) => ({
                            ...prev,
                            [variableName]: value,
                          }));
                        }}
                      />
                    </Form.Group>
                  </Col>
                ))}
              </Row>
            </Card.Body>
          </Card>

          <Card>
            <Card.Header>
              <strong>Rendered Preview</strong>
            </Card.Header>
            <Card.Body>
              <div className="mb-3">
                <div className="text-muted small mb-1">Subject</div>
                <div className="border rounded p-2 bg-light" style={{ whiteSpace: 'pre-wrap' }}>
                  {renderedSubject || <span className="text-muted">(empty)</span>}
                </div>
              </div>

              <div>
                <div className="text-muted small mb-1">Body</div>
                <div className="border rounded p-2 bg-light" style={{ whiteSpace: 'pre-wrap', minHeight: 220 }}>
                  {renderedBody || <span className="text-muted">(empty)</span>}
                </div>
              </div>
            </Card.Body>
          </Card>

          <Card className="mt-3">
            <Card.Header>
              <strong>Study Reminder Trigger</strong>
            </Card.Header>
            <Card.Body>
              <Row className="g-2 mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="small mb-1">Inactive Days</Form.Label>
                    <Form.Control
                      type="number"
                      min={1}
                      max={365}
                      value={reminderDays}
                      onChange={(event) => setReminderDays(event.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="small mb-1">Max Recipients</Form.Label>
                    <Form.Control
                      type="number"
                      min={1}
                      max={2000}
                      value={maxReminderRecipients}
                      onChange={(event) => setMaxReminderRecipients(event.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Button
                variant="outline-primary"
                onClick={handleSendStudyReminders}
                disabled={sendingReminders || loading || !courseId}
              >
                {sendingReminders ? 'Processing Reminders...' : 'Send Study Reminders Now'}
              </Button>

              {reminderResult && (
                <Alert variant="light" className="mt-3 mb-0">
                  <div className="small">
                    Candidates: {reminderResult.candidates} | Attempted: {reminderResult.attempted}
                  </div>
                  <div className="small">
                    Sent: {reminderResult.sent} | Simulated: {reminderResult.simulated}
                  </div>
                  <div className="small">
                    Skipped: {reminderResult.skipped} | Failed: {reminderResult.failed}
                  </div>
                </Alert>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
}

CourseEmailTemplateManager.propTypes = {
  courseId: PropTypes.string,
  courseTitle: PropTypes.string,
};

CourseEmailTemplateManager.defaultProps = {
  courseId: '',
  courseTitle: '',
};
